import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { Permission } from '@/models/Permission';

/**
 * Permission code names for the AR money-movement and statement actions, and the getters that gate
 * them. Each action lives in its own modal or page, so the codes are shared here rather than
 * repeated at every launch point. Values must match PolicyPermissions.AccountsReceivable.
 */
export const ARPermissionCodeNames = {
    postPayment: 'Accounts Receivable/Payment/Post',
    voidPayment: 'Accounts Receivable/Payment/Refund',
    postAdjustment: 'Accounts Receivable/Transaction/Post Adjustment',
    writeOff: 'Accounts Receivable/Transaction/Write Off',
    generateStatement: 'Accounts Receivable/Statement/Generate Single',
    runStatementBatch: 'Accounts Receivable/Statement/Run Batch',
} as const;

const hasPermission = (permissions: Permission[], code: string): boolean =>
    permissions.some(p => p.permissionCodeName === code);

@Component
export default class ARPermissionsMixin extends Vue {
    get canPostPayment(): boolean {
        return hasPermission(this.$permissions, ARPermissionCodeNames.postPayment);
    }

    /** Voiding a posted payment is a refund, and is gated as one. */
    get canVoidPayment(): boolean {
        return hasPermission(this.$permissions, ARPermissionCodeNames.voidPayment);
    }

    get canPostAdjustment(): boolean {
        return hasPermission(this.$permissions, ARPermissionCodeNames.postAdjustment);
    }

    /** The transaction-level write-off, which credits a balance. Closing the whole account is a
     * separate action gated by Accounts Receivable/Account/Close. */
    get canWriteOff(): boolean {
        return hasPermission(this.$permissions, ARPermissionCodeNames.writeOff);
    }

    /** Generating one account's statement on demand. Re-sending an existing statement is gated as
     * the same action — both render and deliver a single statement. */
    get canGenerateStatement(): boolean {
        return hasPermission(this.$permissions, ARPermissionCodeNames.generateStatement);
    }

    get canRunStatementBatch(): boolean {
        return hasPermission(this.$permissions, ARPermissionCodeNames.runStatementBatch);
    }
}
